import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SafeHtml } from "../components/SafeHtml";
import { 
  Shield, Gift, CreditCard, ChevronLeft, ExternalLink, 
  Star, CheckCircle, Clock, DollarSign, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CasinoDetail = () => {
  const { id } = useParams();
  const [brand, setBrand] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBrandDetails();
    fetchSettings();
  }, [id]);

  const fetchBrandDetails = async () => {
    try {
      const res = await axios.get(`${API}/brands/${id}`);
      setBrand(res.data);
    } catch (err) {
      setError("Casino bulunamadı");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (err) {
      console.error("Settings fetch error:", err);
    }
  };

  const trackClick = async () => {
    try {
      await axios.post(`${API}/track-click`, { brand_id: id, click_type: "affiliate" });
    } catch (e) {
      // Silent fail
    }
  };

  const handleVisit = () => {
    // If brand is expired or inactive, redirect to homepage
    if (brand.is_expired || !brand.is_active) {
      window.location.href = '/';
      return;
    }
    trackClick();
    window.open(brand.affiliate_link, '_blank', 'noopener,noreferrer');
  };

  // Check if brand is available (not expired and active)
  const isBrandAvailable = brand && !brand.is_expired && brand.is_active;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-zinc-500">Yükleniyor...</div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f] flex flex-col items-center justify-center gap-4">
        <AlertCircle className="h-16 w-16 text-red-500" />
        <h1 className="text-2xl font-bold text-white">{error || "Casino bulunamadı"}</h1>
        <Link to="/">
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Ana Sayfaya Dön
          </Button>
        </Link>
      </div>
    );
  }

  const ctaText = settings.cta_button_text || "BONUS AL";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f]">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="h-4 w-4" />
            <span>Tüm Casinolar</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Mobile CTA - Only shown on mobile at top */}
          <div className="lg:hidden">
            <Card className={`bg-gradient-to-b ${isBrandAvailable ? 'from-emerald-900/30' : 'from-amber-900/30'} to-zinc-900 border-${isBrandAvailable ? 'emerald' : 'amber'}-800/50`}>
              <CardContent className="p-6 text-center">
                {isBrandAvailable ? (
                  <>
                    <div className="mb-4">
                      <span className="text-zinc-400">Özel Teklif</span>
                      <p className="text-2xl font-bold text-emerald-400">{brand.bonus}</p>
                    </div>

                    <Button
                      onClick={handleVisit}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-6 text-lg group"
                      data-testid="detail-cta-btn-mobile"
                    >
                      <span>{ctaText}</span>
                      <ExternalLink className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <p className="text-xs text-zinc-500 mt-4">
                      18+ | Sorumlu oyun oynayın
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="w-12 h-12 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-3">
                        <AlertCircle className="h-6 w-6 text-amber-500" />
                      </div>
                      <span className="text-amber-400 font-medium">Bu Teklif Artık Geçerli Değil</span>
                    </div>

                    <Link to="/">
                      <Button
                        className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold py-6 text-lg"
                      >
                        <span>Güncel Teklifleri Gör</span>
                        <ChevronLeft className="h-5 w-5 ml-2 rotate-180" />
                      </Button>
                    </Link>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Hero Card */}
            <Card className="bg-zinc-900/80 border-zinc-800 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600/20 to-amber-600/20 p-4 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  {/* Logo */}
                  <div className="w-24 h-16 sm:w-32 sm:h-20 bg-black rounded-xl border border-zinc-700 flex items-center justify-center p-3 shadow-xl flex-shrink-0">
                    {brand.logo_url ? (
                      <img
                        src={`${BACKEND_URL}${brand.logo_url}`}
                        alt={brand.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-3xl sm:text-4xl font-bold text-zinc-400">{brand.name?.charAt(0)}</span>
                    )}
                  </div>

                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 mb-2">
                      <h1 className="text-2xl sm:text-3xl font-chivo font-black text-white break-words">{brand.name}</h1>
                      {brand.rating && (
                        <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded-full flex-shrink-0">
                          <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                          <span className="text-amber-400 text-sm font-bold">{brand.rating}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-emerald-400">
                      <Shield className="h-4 w-4" />
                      <span className="font-medium text-sm sm:text-base">{brand.license} Lisanslı</span>
                    </div>
                  </div>
                </div>
              </div>

              <CardContent className="p-6">
                {/* Bonus Banner */}
                <div className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 rounded-xl p-6 border border-amber-500/20 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="h-6 w-6 text-amber-400" />
                    <span className="text-zinc-400">Hoşgeldin Bonusu</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{brand.bonus}</p>
                </div>

                {/* Brand Description */}
                {brand.description && (
                  <div className="mb-6">
                    <p className="text-zinc-300 leading-relaxed">{brand.description}</p>
                  </div>
                )}

                {/* Quick Info Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {brand.min_deposit && (
                    <div className="bg-zinc-800/50 rounded-lg p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-zinc-500 text-sm">Minimum Depozit</p>
                        <p className="text-white font-semibold">{brand.min_deposit}</p>
                      </div>
                    </div>
                  )}

                  {brand.withdrawal_time && (
                    <div className="bg-zinc-800/50 rounded-lg p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Clock className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-zinc-500 text-sm">Çekim Süresi</p>
                        <p className="text-white font-semibold">{brand.withdrawal_time}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            {brand.payment_methods && brand.payment_methods.length > 0 && (
              <Card className="bg-zinc-900/80 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="h-5 w-5 text-emerald-400" />
                    <h2 className="text-xl font-bold text-white">Ödeme Yöntemleri</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brand.payment_methods.map((method, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-zinc-800 rounded-lg text-zinc-300 text-sm border border-zinc-700"
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Features */}
            {brand.features && brand.features.length > 0 && (
              <Card className="bg-zinc-900/80 border-zinc-800">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-white mb-4">Öne Çıkan Özellikler</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {brand.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-zinc-300">
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pros & Cons */}
            {((brand.pros && brand.pros.length > 0) || (brand.cons && brand.cons.length > 0)) && (
              <Card className="bg-zinc-900/80 border-zinc-800">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-white mb-4">Artılar & Eksiler</h2>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {brand.pros && brand.pros.length > 0 && (
                      <div>
                        <h3 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Artılar
                        </h3>
                        <ul className="space-y-2">
                          {brand.pros.map((pro, idx) => (
                            <li key={idx} className="text-zinc-300 flex items-start gap-2">
                              <span className="text-green-400 mt-1">+</span>
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {brand.cons && brand.cons.length > 0 && (
                      <div>
                        <h3 className="text-red-400 font-medium mb-3 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Eksiler
                        </h3>
                        <ul className="space-y-2">
                          {brand.cons.map((con, idx) => (
                            <li key={idx} className="text-zinc-300 flex items-start gap-2">
                              <span className="text-red-400 mt-1">-</span>
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Custom Content */}
            {brand.custom_content && (
              <Card className="bg-zinc-900/80 border-zinc-800">
                <CardContent className="p-6">
                  <SafeHtml
                    className="content-html"
                    html={brand.custom_content}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar (Desktop Only) */}
          <div className="hidden lg:block space-y-6">
            {/* CTA Card */}
            <Card className={`bg-gradient-to-b ${isBrandAvailable ? 'from-emerald-900/30' : 'from-amber-900/30'} to-zinc-900 border-${isBrandAvailable ? 'emerald' : 'amber'}-800/50 sticky top-4`}>
              <CardContent className="p-6 text-center">
                {isBrandAvailable ? (
                  <>
                    <div className="mb-4">
                      <span className="text-zinc-400">Özel Teklif</span>
                      <p className="text-2xl font-bold text-emerald-400">{brand.bonus}</p>
                    </div>

                    <Button
                      onClick={handleVisit}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-6 text-lg group"
                      data-testid="detail-cta-btn"
                    >
                      <span>{ctaText}</span>
                      <ExternalLink className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <p className="text-xs text-zinc-500 mt-4">
                      18+ | Sorumlu oyun oynayın | Şartlar ve koşullar geçerlidir
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="w-12 h-12 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-3">
                        <AlertCircle className="h-6 w-6 text-amber-500" />
                      </div>
                      <span className="text-amber-400 font-medium">Bu Teklif Artık Geçerli Değil</span>
                      <p className="text-zinc-500 text-sm mt-2">
                        {brand.is_expired ? "Bu kampanyanın süresi dolmuştur." : "Bu casino şu anda aktif değildir."}
                      </p>
                    </div>

                    <Link to="/">
                      <Button
                        className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold py-6 text-lg"
                        data-testid="detail-cta-btn-unavailable"
                      >
                        <span>Güncel Teklifleri Gör</span>
                        <ChevronLeft className="h-5 w-5 ml-2 rotate-180" />
                      </Button>
                    </Link>

                    <p className="text-xs text-zinc-500 mt-4">
                      Ana sayfada güncel kampanyaları bulabilirsiniz
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Trust Indicators */}
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="font-bold text-white mb-4">Güvenlik Bilgileri</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-emerald-400" />
                    <span className="text-zinc-300">{brand.license} Lisanslı</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-zinc-300">SSL Şifreli</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-zinc-300">Sorumlu Kumar Politikası</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-900/80 border-t border-zinc-800 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <p className="text-zinc-500 text-sm text-center">
            {settings.footer_text || `© ${new Date().getFullYear()} ${settings.site_name || "Casino Rehberi"}. Tüm hakları saklıdır.`}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CasinoDetail;
